import * as api from '@/modules/invoices/api'
import { computed, ref, reactive } from 'vue'
import * as types from './types'
import { InvoiceListFilterParameters } from '@/views/_shared/search-list/types'
import moment from 'moment'
import debounce from 'quasar/src/utils/debounce.js';

export * from './types'

export const statusOptions = [
    {
        value: types.InvoiceStatus.All,
        label: 'All'
    },
    {
        value: types.InvoiceStatus.Created,
        label: 'Created'
    },
    {
        value: types.InvoiceStatus.Sending,
        label: 'Sending'
    },
    {
        value: types.InvoiceStatus.Sent,
        label: 'Sent'
    },
    {
        value: types.InvoiceStatus.Failed,
        label: 'Failed'
    },
    {
        value: types.InvoiceStatus.Revoked,
        label: 'Revoked'
    }
]

const invoices = ref<api.GetInvoicesListResponse['results']>([])
const invoicesCount = ref(0)
const currentInvoice = ref<types.Invoice>({} as types.Invoice)
const invoicePreview = ref<types.Invoice>({} as types.Invoice)
const invoicingExternalLogs = ref<types.InvoicingExternalLogs>({} as types.InvoicingExternalLogs)

const defaultSortBy = 'id desc'

const setInvoicesAgreementFilter = (agreementId: string) => {
    window.localStorage.setItem('invoicesAgreementFilter', agreementId)
}

export const invoicesFilter = reactive<InvoiceListFilterParameters>({
    search: {
        search: '',
        status: '',
        agreementName: '',
        organization: '',
        dateFrom: '',
        dateTo: ''
    },
    sortBy: null,
    limit: 10,
    offset: 0
})

const getInvoices = debounce(() =>
    api.getInvoices(
        invoicesFilter.search,
        invoicesFilter.sortBy || defaultSortBy,
        invoicesFilter.limit,
        invoicesFilter.offset
    ).then(
        invoicesList => {
            invoicesCount.value = invoicesList.count
            invoices.value = invoicesList.results
        }
    ), 600)

const getInvoice = async (invoiceId: string) => {
    currentInvoice.value = await api.getInvoice(invoiceId)
    return currentInvoice.value
}

const getInvoicePreview = async (payload: api.InvoicePreviewRequest) => {
    invoicePreview.value = await api.getInvoicePreview(payload)
    return invoicePreview.value
}

const getInvoicingExternalLogs = async (invoiceId: string) => {
    invoicingExternalLogs.value = await api.getInvoicExternalLogs(invoiceId)
}

const withInvoiceUpdate = async (invoiceId: string, callback: () => void) => {
    await callback()
    await getInvoice(invoiceId)
}

const revokeInvoice = async (invoiceId: string) =>
    withInvoiceUpdate(invoiceId, () => api.revokeInvoice(invoiceId))

const getFormattedPrice = (price: number, currency: string) => {
    return (price / 100).toLocaleString('fi-FI', { style: 'currency', currency })
}

const getFormattedDate = (date: string) => {
    return moment(date).format('YYYY-MM-DD')
}

export function useInvoicesModule () {
    return {
        getInvoices,
        invoices: computed(() => invoices.value),
        invoicesCount: computed(() => invoicesCount.value),
        invoicesFilter,
        getInvoice,
        currentInvoice: computed(() => currentInvoice.value),
        getInvoicePreview,
        invoicePreview: computed(() => invoicePreview.value),
        revokeInvoice,
        getFormattedPrice,
        getFormattedDate,
        setInvoicesAgreementFilter,
        createAllInvoices: api.createAllInvoices,
        createInvoiceForAgreement: api.createInvoiceForAgreement,
        invoicingExternalLogs,
        getInvoicingExternalLogs
    }
}
